import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Grid, IconButton, List, ListItem, ListItemText, TextField, ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoneyIcon from '@mui/icons-material/Money';
import PixIcon from '@mui/icons-material/Pix';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { v4 as uuidv4 } from 'uuid';
import { Environment } from '../environment';
import { EstoqueOperacoesService } from '../services/api/estoque/estoqueOperacoes/EstoqueOperacoesService';
import { ConfiguracaoPdvService } from '../services/api/cadastros/ConfiguracaoPdv';
import { ContaBancariaService } from '../services/api/cadastros/ContaBancaria';
import { AutoCompleteContaBancariaSimple } from './AutoCompleteContaBancariaSimples';

export interface IOperacaoPagamentoFixa {
  id: string;
  nome: string;
  icon: React.ReactNode;
  // true pras formas que não têm id de operação fixo no Environment — o id
  // real precisa ser resolvido (ou cadastrado) no backend antes de poder
  // ser usada, ver o useEffect em SeletorFormasPagamento.
  precisaResolverId?: boolean;
}

// Formas de pagamento à vista, mesma lista base do PDV (Dialogvenda.tsx) mais
// as formas comuns numa baixa de contas a pagar/receber. Cheque, Boleto,
// Transferência e Outros não têm id de operação configurado no Environment
// — idoperacao é um uuid referenciado por outras tabelas (ex.: venda), então
// um id inventado quebraria qualquer insert que passasse por lá. O id real
// de cada uma é resolvido (ou criado, se ainda não existir) assim que o
// componente monta.
export const OPERACOES_PAGAMENTO: IOperacaoPagamentoFixa[] = [
  { id: Environment.URL_IDOPERACOES, nome: 'Dinheiro', icon: <MoneyIcon fontSize="small" /> },
  { id: Environment.URL_IDPIX, nome: 'PIX', icon: <PixIcon fontSize="small" /> },
  { id: Environment.URL_IDCARTAO, nome: 'Cartão', icon: <CreditCardIcon fontSize="small" /> },
  { id: '', nome: 'Cheque', icon: <PaymentsIcon fontSize="small" />, precisaResolverId: true },
  { id: '', nome: 'Boleto', icon: <ReceiptIcon fontSize="small" />, precisaResolverId: true },
  { id: '', nome: 'Transferência', icon: <SwapHorizIcon fontSize="small" />, precisaResolverId: true },
  { id: '', nome: 'Outros', icon: <MoreHorizIcon fontSize="small" />, precisaResolverId: true },
];

// Variante usada só em Faturamento/ContasReceber, quando as contas de
// recebíveis de Cartão Crédito/Débito existem no tenant atual (ver
// checagem em DialogBaixa/DialogsendFaturar) — permite rotear o valor pra
// conta certa em vez de cair no "Cartão" genérico. Mesmos idoperacao já
// cadastrados em estoqueoperacoes (Environment.URL_IDCARTAO = Crédito).
export const OPERACOES_PAGAMENTO_CARTAO_SEPARADO: IOperacaoPagamentoFixa[] = [
  { id: Environment.URL_IDOPERACOES, nome: 'Dinheiro', icon: <MoneyIcon fontSize="small" /> },
  { id: Environment.URL_IDPIX, nome: 'PIX', icon: <PixIcon fontSize="small" /> },
  { id: Environment.URL_IDCARTAO, nome: 'Cartão Crédito', icon: <CreditCardIcon fontSize="small" /> },
  { id: 'ca75c7d8-ec8d-4d21-9c9b-d8ec909620ec', nome: 'Cartão Débito', icon: <CreditCardIcon fontSize="small" /> },
  { id: '', nome: 'Cheque', icon: <PaymentsIcon fontSize="small" />, precisaResolverId: true },
  { id: '', nome: 'Boleto', icon: <ReceiptIcon fontSize="small" />, precisaResolverId: true },
  { id: '', nome: 'Transferência', icon: <SwapHorizIcon fontSize="small" />, precisaResolverId: true },
  { id: '', nome: 'Outros', icon: <MoreHorizIcon fontSize="small" />, precisaResolverId: true },
];

// Variante usada só na dialog de Faturamento (ver DialogsendFaturar.tsx):
// PIX Dinâmico e PIX Estático como botões próprios (em vez do botão único
// "PIX" com um toggle escondido) — mais direto pro operador, e sem
// "Outros" (não fazia sentido nesse fluxo). Os dois usam o mesmo idoperacao
// fixo de PIX (Environment.URL_IDPIX); a diferença de roteamento de conta
// (Dinâmico → recebível fixa, Estático → conta da venda) é decidida no
// backend pelo texto da categoria (ver resolverContaRecebivelPorCategoria).
export const OPERACOES_PAGAMENTO_FATURAMENTO: IOperacaoPagamentoFixa[] = [
  { id: Environment.URL_IDOPERACOES, nome: 'Dinheiro', icon: <MoneyIcon fontSize="small" /> },
  { id: Environment.URL_IDPIX, nome: 'PIX Estático', icon: <PixIcon fontSize="small" /> },
  { id: Environment.URL_IDPIX, nome: 'PIX Dinâmico', icon: <PixIcon fontSize="small" /> },
  { id: Environment.URL_IDCARTAO, nome: 'Cartão Crédito', icon: <CreditCardIcon fontSize="small" /> },
  { id: 'ca75c7d8-ec8d-4d21-9c9b-d8ec909620ec', nome: 'Cartão Débito', icon: <CreditCardIcon fontSize="small" /> },
  { id: '', nome: 'Cheque', icon: <PaymentsIcon fontSize="small" />, precisaResolverId: true },
  { id: '', nome: 'Boleto', icon: <ReceiptIcon fontSize="small" />, precisaResolverId: true },
  { id: '', nome: 'Transferência', icon: <SwapHorizIcon fontSize="small" />, precisaResolverId: true },
];

// Variante usada só na baixa de Contas a Pagar — sem Cartão (não faz
// sentido pagar um fornecedor no cartão), sem Cheque (pagamento por cheque
// já tem campo próprio, fora do seletor) e sem Outros. PIX aqui não
// distingue Dinâmico/Estático (isso é sobre o QR Code de quem RECEBE) — é
// só "de qual conta sai o PIX", ver `ocultarTipoPix`/`origemDinheiro` em
// SeletorFormasPagamento. Boleto pede conta bancária igual PIX/Transferência
// (ver `formasComContaObrigatoria` em ContasPagar/DialogBaixa.tsx) — débito
// sai da conta escolhida, com a categoria "Boleto" no histórico do caixa
// (ver contaspagar/Baixa.ts, backend).
export const OPERACOES_PAGAMENTO_CONTASPAGAR: IOperacaoPagamentoFixa[] = [
  { id: Environment.URL_IDOPERACOES, nome: 'Dinheiro', icon: <MoneyIcon fontSize="small" /> },
  { id: Environment.URL_IDPIX, nome: 'PIX', icon: <PixIcon fontSize="small" /> },
  { id: '', nome: 'Boleto', icon: <ReceiptIcon fontSize="small" />, precisaResolverId: true },
  { id: '', nome: 'Transferência', icon: <SwapHorizIcon fontSize="small" />, precisaResolverId: true },
];

export const BANDEIRAS_CARTAO = [
  { id: '01', nome: 'Visa', cor: '#1a1f71' },
  { id: '02', nome: 'Mastercard', cor: '#eb001b' },
  { id: '06', nome: 'Elo', cor: '#00a4e0' },
  { id: '07', nome: 'Hipercard', cor: '#f47b00' },
  { id: '03', nome: 'American Express', cor: '#2e77bc' },
  { id: '99', nome: 'Outros', cor: '#666666' },
];

export interface IFormaPagamentoSelecionada {
  id: string;
  idoperacao: string;
  nome: string;
  valor: number;
  // Só preenchidos quando `capturarDetalhesPagamento` está ativo.
  tband?: string;
  caut?: string;
  idcontadestino?: string;
  // Só pra exibição na lista (nome da conta destino) — não é enviado ao
  // backend, que já recebe idcontadestino.
  contaDestinoLabel?: string;
  // Só Boleto: data prevista em que o banco compensa o valor (formato
  // yyyy-mm-dd, mesmo formato do <input type="date"> abaixo). Boleto sempre
  // cai na conta de recebíveis fixa (ver resolverContaRecebivelPorCategoria,
  // backend) até essa data, quando o job de compensação move o valor pra
  // conta bancária real — não pede idcontadestino, ao contrário de
  // Transferência/PIX Estático.
  datacompensacao?: string;
}

interface ISeletorFormasPagamentoProps {
  // Valor que precisa ser totalmente coberto pela soma das formas.
  total: number;
  formas: IFormaPagamentoSelecionada[];
  onChange: (formas: IFormaPagamentoSelecionada[]) => void;
  disabled?: boolean;
  // Lista de botões a oferecer — default OPERACOES_PAGAMENTO (Cartão único).
  // Passe OPERACOES_PAGAMENTO_CARTAO_SEPARADO pra distinguir Crédito/Débito.
  operacoes?: IOperacaoPagamentoFixa[];
  // Opt-in: quando true, PIX Estático/Transferência passam a pedir a conta
  // bancária (tipo banco) de destino antes de liberar o botão "Adicionar".
  // Desligado por padrão pra não mudar o comportamento das telas que
  // reusam este mesmo componente.
  capturarDetalhesPagamento?: boolean;
  // Opt-in: habilita selecionar a forma de pagamento pelas teclas 1-9,
  // seguindo a ordem da lista `operacoes`. Só usado no dialog de Faturar.
  habilitarAtalhosTeclado?: boolean;
  // Contas a Pagar: quais formas (por `nome`) exigem conta bancária, no
  // lugar da regra padrão (Transferência/PIX Estático, pensada pra dinheiro
  // ENTRANDO em Faturamento/ContasReceber). Passe a lista completa das
  // formas "não físicas" da tela — em Contas a Pagar isso é PIX/Boleto/
  // Transferência, já que o dinheiro tem seu próprio seletor de origem
  // (ver `origemDinheiro`).
  formasComContaObrigatoria?: string[];
  // Contas a Pagar: esconde o toggle Dinâmico/Estático do PIX — não existe
  // "tipo de QR Code" do lado de quem paga, só interessa de qual conta sai.
  ocultarTipoPix?: boolean;
  // Contas a Pagar: quando a forma ativa é Dinheiro, mostra um toggle
  // Sessão aberta/Cofre/outros caixas com saldo no lugar do autocomplete de
  // conta bancária — pagar em espécie sai fisicamente de um desses caixas,
  // nunca de uma conta banco qualquer. Omitido = Dinheiro não pede conta
  // (comportamento atual de Faturamento/ContasReceber, onde Dinheiro sempre
  // cai na sessão).
  origemDinheiro?: {
    sessao: { idconta: string; label: string } | null;
    cofre: { idconta: string; label: string } | null;
    // Demais contas tipo='caixa' (fora a da sessão aberta do operador) que
    // tenham saldo disponível — já filtradas pelo chamador (ver
    // ContasPagar/DialogBaixa.tsx). Cada uma vira um botão a mais no toggle,
    // com o saldo no rótulo.
    caixas?: { idconta: string; label: string; saldo: number }[];
  };
  // Rótulo do campo de conta bancária exibido quando `precisaConta` — default
  // "Conta bancária de destino" (Faturamento/ContasReceber, dinheiro
  // entrando). Contas a Pagar usa "Conta bancária de origem" (dinheiro saindo).
  labelContaBancaria?: string;
  // Opt-in: ao entrar em PIX Estático, pré-preenche a conta bancária com a
  // conta padrão de PIX configurada em Configurações do PDV
  // (idcontapadraopix), com fallback pra autosseleção quando só existe uma
  // conta tipo banco cadastrada — mesmo padrão já usado em Dialogsendnfe.tsx/
  // Dialogsendnfce.tsx. Desligado por padrão (só baixa de Contas a Receber
  // liga por enquanto).
  buscarContaPadraoPix?: boolean;
  // Opt-in: só Contas a Receber (baixa de boleto) pede a data prevista de
  // compensação — Boleto ali cai numa conta de recebíveis fixa até essa
  // data (ver resolverContaRecebivelPorCategoria/CompensarBoletosVencidos,
  // backend). Contas a Pagar não liga isso: pagar um fornecedor em boleto já
  // debita a conta escolhida na hora, sem conta de recebíveis nem data de
  // compensação envolvida.
  capturarDataCompensacaoBoleto?: boolean;
}

// Rótulo numerado de cada etapa da forma ativa (origem/conta → data →
// valor) — deixa explícito em que ordem preencher, em vez de só campos
// soltos empilhados.
const Etapa: React.FC<{ numero: number; titulo: string; children: React.ReactNode }> = ({ numero, titulo, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.7rem',
            fontWeight: 700,
            bgcolor: alpha(theme.palette.primary.main, 0.15),
            color: 'primary.main',
            flexShrink: 0,
          }}
        >
          {numero}
        </Box>
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          {titulo}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

// Divide um valor (venda, baixa de contas a receber/pagar...) em mais de uma
// forma de pagamento (Dinheiro/PIX/Cartão) — mesma lista fixa e mesma
// interação (escolher a forma, informar o valor, adicionar) usada na tela de
// venda. Controlado pelo pai: `formas`/`onChange` guardam a lista, este
// componente só cuida da UI de adicionar/remover.
export const SeletorFormasPagamento: React.FC<ISeletorFormasPagamentoProps> = ({
  total,
  formas,
  onChange,
  disabled,
  operacoes,
  capturarDetalhesPagamento,
  habilitarAtalhosTeclado,
  formasComContaObrigatoria,
  ocultarTipoPix,
  origemDinheiro,
  labelContaBancaria,
  buscarContaPadraoPix,
  capturarDataCompensacaoBoleto,
}) => {
  const theme = useTheme();
  const listaOperacoes = operacoes ?? OPERACOES_PAGAMENTO;
  const nomesParaResolver = useMemo(
    () => listaOperacoes.filter((op) => op.precisaResolverId).map((op) => op.nome),
    [listaOperacoes],
  );

  const [operacaoIndex, setOperacaoIndex] = useState(0);
  const [valorTemp, setValorTemp] = useState(0);
  const [erro, setErro] = useState('');
  const [idsResolvidos, setIdsResolvidos] = useState<Record<string, string>>({});
  const [resolvendoIds, setResolvendoIds] = useState(true);
  // Refs dos itens da lista vertical (modo `habilitarAtalhosTeclado`) — pra
  // rolar o item ativo pra dentro da área visível ao navegar com as setas,
  // mesmo padrão do PDV (Dialogvenda.tsx).
  const listItemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const listaContainerRef = useRef<HTMLDivElement>(null);
  // Refs pra navegação por Enter (modo `habilitarAtalhosTeclado`): da lista
  // de formas, PIX pula pro toggle Dinâmico/Estático; Estático/Transferência
  // pulam pra conta bancária; o resto pula direto pro valor.
  const toggleDinamicoRef = useRef<HTMLButtonElement>(null);
  const toggleEstaticoRef = useRef<HTMLButtonElement>(null);
  const contaInputRef = useRef<HTMLInputElement>(null);
  const dataCompensacaoRef = useRef<HTMLInputElement>(null);
  const valorInputRef = useRef<HTMLInputElement>(null);
  const origemDinheiroRef = useRef<HTMLButtonElement>(null);
  // true só quando o Valor foi preenchido ANTES de pular pra conta bancária
  // (PIX Estático — ver onKeyDown do Valor abaixo): nesse caso, escolher a
  // conta deve adicionar direto em vez de voltar o foco pro Valor de novo.
  // Nos fluxos conta-primeiro (Transferência, PIX Estático via toggle) fica
  // sempre false, e o comportamento antigo (focar o Valor) se mantém.
  const pularParaAdicionarAoEscolherConta = useRef(false);
  // true assim que o operador troca a conta manualmente (inclusive limpar o
  // campo) — impede o useEffect de pré-preenchimento (mais abaixo) de
  // reescrever por cima da escolha dele. Sem isso, limpar o autocomplete pra
  // trocar de conta disparava o efeito de novo (idcontadestino virou '' =
  // "ainda não escolhida") e ele devolvia a conta padrão na hora. Zerado em
  // `limparDetalhes` (troca de forma/depois de adicionar), que é o único
  // momento em que faz sentido o preenchimento automático valer de novo.
  const contaAlteradaManualmenteRef = useRef(false);

  // Tira o foco do que o Dialog focou automaticamente ao abrir (normalmente
  // o primeiro campo de texto) e leva pra lista — senão as setas ficam
  // "presas" nesse campo em vez de navegar entre as formas, igual ao PDV.
  useEffect(() => {
    if (!habilitarAtalhosTeclado) return;
    const timeout = setTimeout(() => listaContainerRef.current?.focus(), 100);
    return () => clearTimeout(timeout);
  }, [habilitarAtalhosTeclado]);

  // Detalhes capturados pra forma atualmente selecionada (zerados a cada
  // troca de forma/adição).
  const [tipoPix, setTipoPix] = useState<'dinamico' | 'estatico'>('estatico');
  const [idcontadestino, setIdcontadestino] = useState('');
  const [contaDestinoLabel, setContaDestinoLabel] = useState('');
  const [datacompensacao, setDatacompensacao] = useState('');
  // Conta bancária padrão de PIX (Configurações do PDV, idcontapadraopix),
  // buscada uma vez quando `buscarContaPadraoPix` está ligado — ref porque só
  // precisa ser lida no momento de pré-preencher, não de re-renderizar nada
  // sozinha (mesmo padrão de contaPadraoPixRef em Dialogsendnfe.tsx).
  const contaPadraoPixRef = useRef({ id: '', label: '' });
  const [carregouContaPadraoPix, setCarregouContaPadraoPix] = useState(false);

  useEffect(() => {
    if (!buscarContaPadraoPix) return;
    let cancelado = false;
    ConfiguracaoPdvService.get().then((resultado) => {
      if (cancelado) return;
      if (!(resultado instanceof Error)) {
        contaPadraoPixRef.current = {
          id: resultado.idcontapadraopix || '',
          label: resultado.contapadraopixTitular || '',
        };
      }
      setCarregouContaPadraoPix(true);
    });
    return () => { cancelado = true; };
  }, [buscarContaPadraoPix]);

  useEffect(() => {
    let cancelado = false;
    EstoqueOperacoesService.resolverPorDescricao(nomesParaResolver).then((resultado) => {
      if (cancelado) return;
      setResolvendoIds(false);
      if (!(resultado instanceof Error)) setIdsResolvidos(resultado);
    });
    return () => { cancelado = true; };
  }, [nomesParaResolver.join(',')]);

  const somaAtual = formas.reduce((acc, f) => acc + f.valor, 0);
  const faltaPagar = Math.max(0, Number((total - somaAtual).toFixed(2)));

  // Valor já chega preenchido com o total enquanto nada foi adicionado —
  // antes a primeira forma (ativa por padrão) abria com o Valor vazio, e o
  // operador precisava digitar o total inteiro à mão.
  useEffect(() => {
    if (formas.length === 0) setValorTemp(total);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const operacaoAtual = listaOperacoes[operacaoIndex];
  const nomeOperacaoAtual = operacaoAtual?.nome ?? '';
  // Duas formas de chegar num pagamento PIX: o botão genérico "PIX" (usado
  // nas baixas de contas a receber/pagar), que abre um toggle Dinâmico/
  // Estático embaixo, ou os botões já explícitos "PIX Dinâmico"/"PIX
  // Estático" (ver OPERACOES_PAGAMENTO_FATURAMENTO) — só a Faturamento usa
  // esse segundo jeito, sem precisar do toggle nem de conta destino manual
  // (o Estático fixo sempre cai na conta da venda, ver backend).
  const isPixComToggle = nomeOperacaoAtual === 'PIX';
  const isPixEstaticoFixo = nomeOperacaoAtual === 'PIX Estático';
  const isPixDinamicoFixo = nomeOperacaoAtual === 'PIX Dinâmico';
  const isPix = isPixComToggle || isPixEstaticoFixo || isPixDinamicoFixo;
  const tipoPixEfetivo: 'dinamico' | 'estatico' = isPixEstaticoFixo ? 'estatico' : isPixDinamicoFixo ? 'dinamico' : tipoPix;
  const isTransferencia = nomeOperacaoAtual === 'Transferência';
  const precisaDataCompensacao = capturarDataCompensacaoBoleto && nomeOperacaoAtual === 'Boleto';
  // PIX Estático (tanto o toggle genérico quanto o botão fixo só de
  // Faturamento) sempre pede a conta bancária de destino — é o QR Code fixo
  // de uma conta específica, então precisa saber qual. Cartão Crédito/
  // Débito e PIX Dinâmico não pedem mais bandeira/código de autorização/ID
  // da transação — só a conta é obrigatória. Contas a Pagar substitui essa
  // regra por `formasComContaObrigatoria` (PIX/Boleto/Transferência, já que
  // lá o dinheiro sai, não entra).
  const precisaConta = capturarDetalhesPagamento && (
    formasComContaObrigatoria
      ? formasComContaObrigatoria.includes(nomeOperacaoAtual)
      : (isTransferencia || (isPix && tipoPixEfetivo === 'estatico'))
  );
  // Só em Contas a Pagar (`origemDinheiro` presente): Dinheiro pede a
  // escolha entre a sessão de caixa aberta e o Cofre, no lugar da conta
  // bancária livre acima.
  const precisaOrigemDinheiro = !!origemDinheiro && nomeOperacaoAtual === 'Dinheiro';

  // Pré-preenche a conta assim que o PIX Estático fica ativo (toggle
  // visível, ver `!ocultarTipoPix` abaixo, ou botão fixo) ou quando a forma é
  // Boleto (Contas a Pagar) — conta padrão configurada no PDV; se não houver
  // nenhuma configurada, cai no mesmo fallback de Dialogsendnfe.tsx:
  // autosseleciona quando só existe 1 conta tipo banco cadastrada. Só roda
  // com `buscarContaPadraoPix` ligado, e só se a conta ainda não foi
  // escolhida (não sobrescreve uma troca manual do operador). O PIX de
  // Contas a Pagar fica de fora da 1ª condição de propósito: lá o toggle
  // Dinâmico/Estático nem aparece (`ocultarTipoPix`) — "estático" seria só o
  // valor padrão interno de `tipoPix`, nunca uma escolha real do operador.
  useEffect(() => {
    if (!buscarContaPadraoPix || !carregouContaPadraoPix) return;
    if (contaAlteradaManualmenteRef.current) return;
    if (!precisaConta || idcontadestino) return;
    const isPixEstaticoEscolhido = isPix && tipoPixEfetivo === 'estatico' && !ocultarTipoPix;
    if (!isPixEstaticoEscolhido && nomeOperacaoAtual !== 'Boleto') return;
    if (contaPadraoPixRef.current.id) {
      setIdcontadestino(contaPadraoPixRef.current.id);
      setContaDestinoLabel(contaPadraoPixRef.current.label);
      return;
    }
    let cancelado = false;
    ContaBancariaService.getAllList('', '', 'banco', 50).then((result) => {
      if (cancelado || result instanceof Error || result.data.length !== 1) return;
      setIdcontadestino(result.data[0].idconta);
      setContaDestinoLabel(result.data[0].numeroconta || result.data[0].titular || '');
    });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscarContaPadraoPix, carregouContaPadraoPix, precisaConta, isPix, tipoPixEfetivo, ocultarTipoPix, nomeOperacaoAtual, idcontadestino]);

  const limparDetalhes = () => {
    setTipoPix('estatico');
    setIdcontadestino('');
    setContaDestinoLabel('');
    setDatacompensacao('');
    contaAlteradaManualmenteRef.current = false;
  };

  const selecionarOperacao = (index: number) => {
    setOperacaoIndex(index);
    limparDetalhes();
    if (faltaPagar > 0) setValorTemp(faltaPagar);
    setErro('');
  };

  // Enter na lista de formas: Dinheiro (Contas a Pagar) pula pra origem,
  // formas que exigem conta bancária (Transferência, Boleto, PIX Estático —
  // via `precisaConta`, que já resolve tudo isso reativamente) pulam pra
  // conta primeiro (conta em cima, valor embaixo — a conta geralmente já vem
  // preenchida pela conta padrão do PDV, ver buscarContaPadraoPix), as
  // demais vão direto pro Valor. Exceção: PIX Estático (botão fixo, só em
  // Faturamento) mantém o fluxo valor-primeiro de antes (a pedido, nessa
  // tela especificamente). Sempre joga o valor que falta pagar pro campo
  // Valor antes de mudar o foco — cobre tanto o item já ativo por padrão
  // (nunca passou por selecionarOperacao) quanto o caso normal de navegação
  // com as setas.
  const handleEnterNaLista = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    if (faltaPagar > 0) setValorTemp(faltaPagar);
    const operacao = listaOperacoes[operacaoIndex];
    if (!operacao || !capturarDetalhesPagamento) {
      valorInputRef.current?.focus();
      return;
    }
    if (operacao.nome === 'Dinheiro' && origemDinheiro) {
      origemDinheiroRef.current?.focus();
      return;
    }
    if (precisaConta && operacao.nome !== 'PIX Estático') {
      contaInputRef.current?.focus();
    } else if (capturarDataCompensacaoBoleto && operacao.nome === 'Boleto') {
      dataCompensacaoRef.current?.focus();
    } else {
      valorInputRef.current?.focus();
    }
  };

  // Enter no toggle Dinâmico/Estático: Estático sem conta ainda escolhida
  // pula pra ela (marca `pularParaAdicionarAoEscolherConta` pra adicionar
  // direto assim que ela vier, ver AutoCompleteContaBancariaSimple abaixo);
  // nos demais casos (Dinâmico, ou Estático com conta já preenchida pela
  // conta padrão do PDV) o Valor já foi preenchido antes de chegar aqui
  // (conta vem antes dele agora), então adiciona direto.
  const handleEnterNoToggle = (event: React.KeyboardEvent) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    if (tipoPix === 'estatico' && !idcontadestino) {
      pularParaAdicionarAoEscolherConta.current = true;
      contaInputRef.current?.focus();
      return;
    }
    adicionar();
  };

  // Máscara "de trás pra frente": cada dígito digitado entra como centavo e
  // empurra os anteriores pra esquerda — não precisa digitar vírgula, o
  // valor vai se ajustando sozinho conforme digita (e apagando também).
  const handleChangeValor = (texto: string) => {
    const digitos = texto.replace(/\D/g, '');
    setValorTemp(digitos ? Number(digitos) / 100 : 0);
  };

  const valorExibido = valorTemp === 0
    ? ''
    : valorTemp.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const adicionar = () => {
    const operacao = listaOperacoes[operacaoIndex];
    if (!operacao) return;
    const idoperacao = operacao.precisaResolverId ? idsResolvidos[operacao.nome] : operacao.id;
    if (!idoperacao) { setErro('Aguarde, carregando as formas de pagamento...'); return; }
    if (valorTemp <= 0) { setErro('Informe um valor válido!'); return; }
    if (somaAtual + valorTemp > total + 0.01) {
      setErro(`Valor excede o total. Limite máximo: ${(total - somaAtual).toFixed(2)}`);
      return;
    }
    if (precisaOrigemDinheiro && !idcontadestino) {
      setErro('Selecione de onde sai o dinheiro');
      return;
    }
    if (precisaConta && !idcontadestino) {
      setErro(labelContaBancaria ? `Selecione a ${labelContaBancaria.toLowerCase()}` : 'Selecione a conta bancária de destino');
      return;
    }
    if (precisaDataCompensacao && !datacompensacao) {
      setErro('Informe a data prevista de compensação do boleto');
      return;
    }
    setErro('');
    onChange([...formas, {
      id: uuidv4(),
      idoperacao,
      nome: isPixComToggle && !ocultarTipoPix ? `PIX ${tipoPix === 'dinamico' ? 'Dinâmico' : 'Estático'}` : operacao.nome,
      valor: valorTemp,
      idcontadestino: (precisaConta || precisaOrigemDinheiro) ? idcontadestino : undefined,
      contaDestinoLabel: (precisaConta || precisaOrigemDinheiro) ? contaDestinoLabel : undefined,
      datacompensacao: precisaDataCompensacao ? datacompensacao : undefined,
    }]);
    // Já sugere o que ainda falta pra próxima forma (pagamento dividido).
    setValorTemp(Math.max(0, Number((total - somaAtual - valorTemp).toFixed(2))));
    limparDetalhes();
  };

  // Consome `pularParaAdicionarAoEscolherConta`: dispara só depois que
  // `idcontadestino` já reflete a conta recém-escolhida (senão `adicionar()`
  // leria o valor antigo, de antes da seleção — closure do onChange do
  // Autocomplete é da render anterior).
  useEffect(() => {
    if (pularParaAdicionarAoEscolherConta.current && idcontadestino) {
      pularParaAdicionarAoEscolherConta.current = false;
      adicionar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idcontadestino]);

  const remover = (id: string) => {
    onChange(formas.filter((item) => item.id !== id));
  };

  // Rola o item ativo pra dentro da área visível da lista — mesmo efeito do
  // PDV ao navegar com as setas.
  useEffect(() => {
    if (!habilitarAtalhosTeclado) return;
    listItemRefs.current[operacaoIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [habilitarAtalhosTeclado, operacaoIndex]);

  useEffect(() => {
    if (!habilitarAtalhosTeclado) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      const alvo = event.target as HTMLElement | null;
      // Não intercepta enquanto o usuário digita num campo de texto, ou
      // enquanto navega dentro de um Select/Autocomplete aberto (bandeira,
      // conta bancária) — senão as setas brigam com o menu deles.
      // Também não intercepta com foco no toggle Dinâmico/Estático — senão
      // trocar de forma no meio da escolha do tipo de PIX derruba o toggle
      // (que some da tela) com o foco ainda nele.
      const digitando = !!alvo && (
        ['INPUT', 'TEXTAREA'].includes(alvo.tagName) ||
        !!alvo.closest('[role="combobox"], [role="listbox"], [role="group"], .MuiAutocomplete-root, .MuiPopover-root')
      );
      if (digitando) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        selecionarOperacao(Math.min(listaOperacoes.length - 1, operacaoIndex + 1));
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        selecionarOperacao(Math.max(0, operacaoIndex - 1));
        return;
      }
      const numero = Number(event.key);
      if (!Number.isInteger(numero) || numero < 1 || numero > listaOperacoes.length) return;
      event.preventDefault();
      selecionarOperacao(numero - 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [habilitarAtalhosTeclado, listaOperacoes.length, faltaPagar, operacaoIndex]);

  // Lista vertical no estilo do PDV (Dialogvenda.tsx): item ativo sempre o
  // primeiro ao abrir, navegação com ↑/↓ e atalho numérico, ícone + nome +
  // chip do atalho. Usada como coluna própria quando `habilitarAtalhosTeclado`.
  const listaVertical = (
    <Box
      ref={listaContainerRef}
      tabIndex={0}
      onKeyDown={handleEnterNaLista}
      sx={{
        maxHeight: { xs: 220, md: 320 },
        overflowY: 'auto',
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        borderRadius: 2,
        outline: 'none',
      }}
    >
      {listaOperacoes.map((op, index) => {
        const isActive = operacaoIndex === index;
        const aguardandoId = !!op.precisaResolverId && resolvendoIds;
        return (
          <Box
            key={op.nome}
            ref={(el: HTMLDivElement | null) => { listItemRefs.current[index] = el; }}
            onClick={() => { if (!disabled && !aguardandoId) selecionarOperacao(index); }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.5,
              py: 1,
              cursor: disabled || aguardandoId ? 'default' : 'pointer',
              borderLeft: `2px solid ${isActive ? theme.palette.primary.main : 'transparent'}`,
              bgcolor: isActive ? alpha(theme.palette.primary.main, 0.09) : 'transparent',
              opacity: aguardandoId ? 0.6 : 1,
              transition: 'background 0.12s',
              '&:hover': {
                bgcolor: isActive
                  ? alpha(theme.palette.primary.main, 0.14)
                  : alpha(theme.palette.text.primary, 0.04),
              },
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isActive ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.06),
                color: isActive ? theme.palette.primary.contrastText : 'text.secondary',
                flexShrink: 0,
              }}
            >
              {aguardandoId ? <CircularProgress size={14} /> : op.icon}
            </Box>
            <Typography
              variant="body2"
              fontWeight={isActive ? 600 : 400}
              sx={{ flex: 1, color: isActive ? 'primary.main' : 'text.primary' }}
            >
              {op.nome}
            </Typography>
            {index < 9 && (
              <Chip
                label={index + 1}
                size="small"
                sx={{
                  height: 18,
                  minWidth: 18,
                  fontSize: '0.65rem',
                  fontFamily: 'monospace',
                  '& .MuiChip-label': { px: 0.6 },
                  bgcolor: isActive ? alpha(theme.palette.primary.main, 0.25) : alpha(theme.palette.text.primary, 0.08),
                  color: isActive ? 'primary.main' : 'text.secondary',
                }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );

  const linhaBotoes = (
    <Grid container spacing={1}>
      {listaOperacoes.map((op, index) => {
        const isActive = operacaoIndex === index;
        const aguardandoId = !!op.precisaResolverId && resolvendoIds;
        return (
          <Grid item key={op.nome}>
            <Button
              size="small"
              variant={isActive ? 'contained' : 'outlined'}
              startIcon={aguardandoId ? <CircularProgress size={14} /> : op.icon}
              onClick={() => selecionarOperacao(index)}
              disabled={disabled || aguardandoId}
            >
              {op.nome}
            </Button>
          </Grid>
        );
      })}
    </Grid>
  );

  // Valor a informar + botão de adicionar, painel de bandeira/autorização/
  // conta destino, erro, e a lista do que já foi adicionado — tudo que fica
  // "ao lado" da lista de formas quando ela vira uma coluna própria.
  // Numeração das etapas da forma ativa: só conta as que aparecem pra ela
  // (Dinheiro com origem, Boleto/PIX/Transferência com conta, Boleto de
  // Contas a Receber com data) — o Valor é sempre a última.
  const etapaConta = (precisaOrigemDinheiro || precisaConta) ? 1 : 0;
  const etapaData = precisaDataCompensacao ? etapaConta + 1 : 0;
  const etapaValor = Math.max(etapaConta, etapaData) + 1;
  const nomeExibidoOperacao = isPixComToggle && capturarDetalhesPagamento && !ocultarTipoPix
    ? `PIX ${tipoPix === 'dinamico' ? 'Dinâmico' : 'Estático'}`
    : nomeOperacaoAtual;

  const conteudoValorEPagamentos = (
    <>
      {/* Cabeçalho da forma ativa: deixa claro O QUE está sendo adicionado
          e quanto ainda falta cobrir, antes dos campos de conta/valor. */}
      {operacaoAtual && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
            p: 1,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              flexShrink: 0,
            }}
          >
            {operacaoAtual.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              Adicionando pagamento em
            </Typography>
            <Typography variant="body2" fontWeight={700} color="primary.main" noWrap>
              {nomeExibidoOperacao}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
              Falta pagar
            </Typography>
            <Typography variant="body2" fontWeight={700} color={faltaPagar > 0.01 ? 'warning.main' : 'success.main'}>
              {faltaPagar.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Contas a Pagar: Dinheiro sai fisicamente da sessão de caixa aberta,
          do Cofre, ou de outro caixa (tipo='caixa') que tenha saldo — nunca
          de uma conta banco qualquer, então é um toggle fechado, não o
          autocomplete livre usado pras outras formas. */}
      {precisaOrigemDinheiro && (
        <Etapa numero={etapaConta} titulo="De onde sai o dinheiro">
          <ToggleButtonGroup
            exclusive
            size="small"
            value={idcontadestino}
            onChange={(_, valor) => {
              if (!valor) return;
              const label = valor === origemDinheiro?.sessao?.idconta
                ? origemDinheiro?.sessao?.label
                : valor === origemDinheiro?.cofre?.idconta
                  ? origemDinheiro?.cofre?.label
                  : origemDinheiro?.caixas?.find((c) => c.idconta === valor)?.label;
              setIdcontadestino(valor);
              setContaDestinoLabel(label ?? '');
              setTimeout(() => valorInputRef.current?.focus(), 0);
            }}
          >
            {origemDinheiro?.sessao && (
              <ToggleButton ref={origemDinheiroRef} value={origemDinheiro.sessao.idconta} disabled={disabled}>
                Sessão aberta{origemDinheiro.sessao.label ? ` (${origemDinheiro.sessao.label})` : ''}
              </ToggleButton>
            )}
            {origemDinheiro?.cofre && (
              <ToggleButton value={origemDinheiro.cofre.idconta} disabled={disabled}>
                Cofre
              </ToggleButton>
            )}
            {origemDinheiro?.caixas?.map((caixa) => (
              <ToggleButton key={caixa.idconta} value={caixa.idconta} disabled={disabled}>
                {caixa.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Etapa>
      )}

      {/* Conta bancária em cima, Valor embaixo — quando a forma exige conta
          (Transferência, Boleto, PIX Estático via toggle), ela já chega
          pré-preenchida pela conta padrão do PDV quando `buscarContaPadraoPix`
          está ligado (ver useEffect acima), então normalmente o operador só
          confirma e desce pro Valor. Cartão Crédito/Débito e PIX Dinâmico não
          pedem mais bandeira/código de autorização/ID da transação — só a
          conta bancária continua obrigatória pras formas em
          `formasComContaObrigatoria` (ou PIX Estático/Transferência, regra
          padrão), que é roteamento de valor, não um detalhe de auditoria. */}
      {precisaConta && (
        <Etapa numero={etapaConta} titulo={`Escolha a ${(labelContaBancaria ?? 'Conta bancária de destino').toLowerCase()}`}>
          <AutoCompleteContaBancariaSimple
            label={labelContaBancaria ?? 'Conta bancária de destino'}
            value={idcontadestino}
            inputRef={contaInputRef}
            onChange={(id, label) => {
              contaAlteradaManualmenteRef.current = true;
              setIdcontadestino(id);
              setContaDestinoLabel(label ?? '');
              // Fluxo conta-primeiro (Transferência, Boleto, PIX Estático via
              // toggle): valor ainda vazio, volta o foco pra ele. Só o PIX
              // Estático como botão fixo (Faturamento) é valor-primeiro — lá
              // o valor já foi preenchido antes de chegar aqui, então
              // `pularParaAdicionarAoEscolherConta` (setado no onKeyDown do
              // Valor) faz adicionar direto em vez de passar por aqui.
              if (id && !pularParaAdicionarAoEscolherConta.current) {
                setTimeout(() => valorInputRef.current?.focus(), 50);
              }
            }}
            tipo="banco"
            disabled={disabled}
          />
        </Etapa>
      )}

      {/* Data prevista de compensação (só Boleto, Contas a Receber) — o
          valor cai na conta de recebíveis de Boleto até essa data, quando o
          job de compensação (backend) move pra conta bancária real. */}
      {precisaDataCompensacao && (
        <Etapa numero={etapaData} titulo="Informe quando o boleto compensa">
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Data prevista de compensação"
            value={datacompensacao}
            onChange={(e) => setDatacompensacao(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || !datacompensacao) return;
              e.preventDefault();
              valorInputRef.current?.focus();
            }}
            inputRef={dataCompensacaoRef}
            disabled={disabled}
            InputLabelProps={{ shrink: true }}
          />
        </Etapa>
      )}

      <Etapa numero={etapaValor} titulo={`Confirme o valor em ${nomeExibidoOperacao || 'pagamento'}`}>
        <Box display="flex" gap={1}>
          <TextField
            size="small"
            label="Valor"
            placeholder="0,00"
            value={valorExibido}
            inputRef={valorInputRef}
            onChange={(e) => handleChangeValor(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              // PIX Estático (botão fixo, só em Faturamento) chega aqui sem
              // conta ainda escolhida (ver handleEnterNaLista) — Enter no Valor
              // pula pra ela em vez de tentar adicionar direto. As demais
              // formas com conta obrigatória já vêm com idcontadestino
              // preenchido nesse ponto (a conta é pedida antes do valor pra
              // elas agora), então esse desvio não afeta o fluxo delas.
              if (precisaConta && !idcontadestino) {
                pularParaAdicionarAoEscolherConta.current = true;
                contaInputRef.current?.focus();
                return;
              }
              adicionar();
            }}
            inputProps={{ inputMode: 'numeric' }}
            disabled={disabled}
            sx={{ flex: 1, minWidth: 120 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon fontSize="small" />}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            onClick={adicionar}
            disabled={disabled}
          >
            Adicionar
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Enter no valor também adiciona. Para dividir o pagamento, adicione outra forma com o restante.
        </Typography>
      </Etapa>

      {/* Escolha Dinâmico/Estático fica embaixo do Valor — mesma ordem da
          dialog de emitir NFe (Dialogsendnfe.tsx). Estático é o padrão (ver
          useState de tipoPix acima), então a conta acima já reflete isso
          desde que a forma "PIX" é selecionada; esse toggle serve pra trocar
          pra Dinâmico quando for o caso. */}
      {isPixComToggle && capturarDetalhesPagamento && !ocultarTipoPix && (
        <Box onKeyDown={handleEnterNoToggle} sx={{ mb: 1.5 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={tipoPix}
            onChange={(_, valor) => {
              if (!valor) return;
              setTipoPix(valor);
              if (!(valor === 'estatico' && idcontadestino)) {
                setIdcontadestino('');
                setContaDestinoLabel('');
              }
              setTimeout(() => {
                (valor === 'estatico' && !idcontadestino ? contaInputRef : valorInputRef).current?.focus();
              }, 0);
            }}
          >
            <ToggleButton ref={toggleEstaticoRef} value="estatico" disabled={disabled}>PIX Estático</ToggleButton>
            <ToggleButton ref={toggleDinamicoRef} value="dinamico" disabled={disabled}>PIX Dinâmico</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      {erro && (
        <Alert severity="warning" sx={{ mb: 1 }} onClose={() => setErro('')}>
          {erro}
        </Alert>
      )}

      <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        Pagamentos adicionados
      </Typography>
      <List dense disablePadding>
        {formas.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            Nenhuma forma adicionada.
          </Typography>
        ) : formas.map((item) => (
          <ListItem
            key={item.id}
            divider
            secondaryAction={
              <IconButton edge="end" size="small" onClick={() => remover(item.id)} disabled={disabled}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            }
          >
            <ListItemText
              primary={item.nome}
              secondary={[
                item.tband ? BANDEIRAS_CARTAO.find((b) => b.id === item.tband)?.nome : null,
                item.caut ? `Aut.: ${item.caut}` : null,
                item.contaDestinoLabel,
                item.datacompensacao ? `Compensa em ${new Date(`${item.datacompensacao}T00:00:00`).toLocaleDateString('pt-BR')}` : null,
              ].filter(Boolean).join(' • ') || undefined}
            />
            <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
              {item.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </Typography>
          </ListItem>
        ))}
      </List>

      {formas.length > 0 && (
        <Box display="flex" justifyContent="flex-end" mt={0.5}>
          <Typography variant="caption" fontWeight={600} color={faltaPagar > 0.01 ? 'warning.main' : 'success.main'}>
            {faltaPagar > 0.01
              ? `Falta: ${faltaPagar.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`
              : 'Total coberto'}
          </Typography>
        </Box>
      )}
    </>
  );

  if (habilitarAtalhosTeclado) {
    // Mesma divisão em colunas da dialog de Finalizar Venda do PDV: a
    // listagem de formas de pagamento vira uma coluna própria (fixa),
    // com o valor/pagamentos adicionados ocupando a coluna central,
    // maior, ao lado dela.
    return (
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
        <Box sx={{ width: { xs: '100%', md: 210 }, flexShrink: 0 }}>
          {listaVertical}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {conteudoValorEPagamentos}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {linhaBotoes}
      <Box sx={{ mt: 1.5 }}>
        {conteudoValorEPagamentos}
      </Box>
    </Box>
  );
};
